import { useState } from 'react';
import { mergeProblem, type Doctor } from '@/domain';
import { useMergeDoctor } from '@/application/usecases/useClients';
import { strings } from '@/shared/strings';
import { ConfirmSheet } from '@/ui/primitives/ConfirmSheet';
import { toast } from '@/ui/primitives/toastStore';
import { formRefusal, readFailure, statusOf } from './clientRefusal';
import { DoctorPicker, type PickedDoctor } from './DoctorPicker';
import styles from './ClientForm.module.css';

/** The one thing a merge names, by the name the server refuses it under. */
const FIELDS = ['duplicateId'] as const;

export interface MergeDoctorSheetProps {
  /** The doctor that is kept: the profile the merge was asked from. */
  keep: Doctor;
  /**
   * The doctor to merge into it, when the list of possible duplicates already
   * says which. Left off to choose any doctor, with a search.
   */
  duplicate?: PickedDoctor;
  onClose: () => void;
  /** The doctor kept, as the server answered it: with the cases that moved and the details that were filled in. */
  onMerged: (kept: Doctor) => void;
  /** One of the two is no longer what the screen shows: read the profile and its possible duplicates again. */
  onStale?: () => void;
}

/**
 * Merging one doctor into another. The request of 6 October 2026 (third),
 * item 1: similar names became separate doctors, and the lab makes them one.
 *
 * The confirmation itself, built on the shared `ConfirmSheet`, and the same
 * sheet both ways in: from Merge beside a possible duplicate, where the doctor
 * to merge is already said, and from Merge another doctor into this one, where
 * it is searched for first and the question fills in as soon as one is picked.
 * So there is never a second question on top of a form.
 *
 * It asks whoever is there, whatever their role. A merge moves every case of
 * one doctor to the other and removes a record, and there is no way back: the
 * question names both doctors by name and by ID, says how many cases move, and
 * says it cannot be undone. The confirm locks on the first tap.
 *
 * A refusal is the server's, in its words, and stays in the sheet: under the
 * search when the doctor to merge is what is wrong, under the question
 * otherwise.
 */
export function MergeDoctorSheet({
  keep,
  duplicate,
  onClose,
  onMerged,
  onStale,
}: MergeDoctorSheetProps) {
  const merge = useMergeDoctor();
  const choosing = duplicate === undefined;
  const [picked, setPicked] = useState<PickedDoctor | null>(duplicate ?? null);
  const [error, setError] = useState<string | null>(null);

  function confirm(): boolean {
    // Before it is sent: a doctor is named, and it is not the doctor being kept.
    const problem = mergeProblem(keep.id, picked?.id);
    if (picked === null || problem !== null) {
      setError(problem);
      return false;
    }

    setError(null);
    merge.mutate(
      { keepId: keep.id, duplicateId: picked.id },
      {
        onSuccess: (kept) => {
          toast(strings.clients.doctorMerged(picked.code, kept.code), { tone: 'success' });
          onMerged(kept);
        },
        onError: (failure: unknown) => {
          const refusal = formRefusal(failure, FIELDS);
          setError(refusal?.fields.duplicateId ?? refusal?.message ?? readFailure(failure));
          // Gone or changed since the screen read them: the kept doctor (404), or the one to merge.
          if (statusOf(failure) === 404 || refusal?.fields.duplicateId !== undefined) onStale?.();
        },
      },
    );
    return true;
  }

  return (
    <ConfirmSheet
      title={strings.clients.mergeTitle(keep.name)}
      statement={
        picked === null
          ? strings.clients.mergeChoose(keep.name, keep.code)
          : strings.clients.confirmMerge(picked, keep, picked.caseCount)
      }
      confirmLabel={strings.clients.merge}
      cancelLabel={strings.clients.cancel}
      closeLabel={strings.sheet.close}
      onConfirm={confirm}
      onCancel={onClose}
      busy={merge.isPending}
      // Nothing to confirm until there is a doctor to merge.
      confirmDisabled={picked === null}
    >
      {choosing ? (
        <DoctorPicker
          label={strings.clients.mergeDoctorLabel}
          value={picked}
          error={error}
          // A doctor is not merged with itself, so this one is never offered.
          exclude={keep.id}
          autoFocus
          onChange={(next) => {
            setPicked(next);
            // Whatever was wrong with the last choice is not wrong with this one.
            setError(null);
          }}
        />
      ) : (
        error !== null && (
          <p className={styles.message} role="alert">
            {error}
          </p>
        )
      )}
    </ConfirmSheet>
  );
}
